import { Table, type TablePaginationConfig } from "antd";
import type { ColumnFilterItem, FilterValue, Key, SorterResult } from "antd/es/table/interface";
import type { TableProps } from "antd/lib/table";
import type { ColumnType } from "antd/lib/table/interface";
import { useEffect, useMemo, useState } from "react";

import { canAccess } from "../../../core/router/auth/permissions";
import type { Entity } from "../../../core/types/fe";
import type { FormComponent, ValidationRule } from "../../Form/types/types";
import type { Datasource } from "../types/Datasource";

export type ExtendedColumnsType<T> = ColumnType<T> & {
    useUniqueValuesAsFilter?: boolean;
    searchAutoComplete?: boolean;
    key: string;
    editable?: boolean;
    editComponent?: FormComponent;
    validation?: ValidationRule[];
    urlToFetchOptions?: string;
    optionDisplayPropName?: string;
    optionValuePropName?: string;
    dataIndex?: string;
    hidden?: boolean;
};

export interface ResourceTableProps<T extends Entity> {
    onRowSelectionChange?: (selectedRows: T[]) => void;
    onStateChanged?: (sorter: SorterResult<T>, pageSize: number, currentPage: number) => void;
    datasource?: Datasource<T>;
    resource?: string;
    selectedRowKeys?: Key[];
}

export interface GenericTableProps<T extends Entity> {
    getColumnsConfig: (sortInfo: SorterResult<T>) => ExtendedColumnsType<T>[];
    datasource: Datasource<T>;
    onSelectionCallback?: Function;
    onStateChanged?: Function;
    onRow?: TableProps<T>["onRow"];
    name?: string;
    resource?: string;
    rowClassName?: (record: T) => string;
    selectedRowKeys?: Key[];
}

export const GenericTable = <T extends object>({
    getColumnsConfig,
    datasource,
    onSelectionCallback,
    onStateChanged,
    onRow,
    name,
    resource,
    rowClassName,
    selectedRowKeys: externalSelectedRowKeys,
}: GenericTableProps<T>) => {
    const [pageSize, setPageSize] = useState(datasource.pageSize);
    const [currentPage, setCurrentPage] = useState(datasource.pageNumber);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [, setFilterInfo] = useState<Record<string, FilterValue | null>>({});
    const [sortInfo, setSortInfo] = useState<SorterResult<T>>({});

    const hasAccess =
        resource === "terminal-versions" ||
        resource === "user-logs" ||
        !resource ||
        canAccess(resource, "basicTabsRead") ||
        canAccess(resource, "canWrite");
    const columns = useMemo(() => getColumnsConfig(sortInfo), [sortInfo, getColumnsConfig]);
    const columnConfig = extendColumnConfig<T>(columns, (datasource?.data || []) as readonly T[]);

    // externalSelectedRowKeys are selections that are not made by the user on the table. e.g. in CheckAllAffectedButton all items that fit a criteria are selected in the table
    useEffect(() => {
        if (externalSelectedRowKeys) {
            setSelectedRowKeys(externalSelectedRowKeys);
        }
    }, [externalSelectedRowKeys]);

    useEffect(() => {
        setPageSize(datasource.pageSize);
        setCurrentPage(datasource.pageNumber);
        setSelectedRowKeys([]);
    }, [datasource.pageNumber, datasource.pageSize]);

    const onChange = (
        paginationData: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<T> | SorterResult<T>[],
    ) => {
        setFilterInfo(filters);
        setSortInfo(sorter as SorterResult<T>);
        setPageSize(paginationData.pageSize);
        setCurrentPage(paginationData.current);

        if (onStateChanged) {
            onStateChanged(sorter, paginationData);
        }
    };

    const paginationFinal = {
        pageSize,
        current: currentPage,
        placement: ["bottomCenter"],
        showSizeChanger: true,
    } as TablePaginationConfig;
    if (datasource?.totalNumberOfResults) {
        paginationFinal.total = datasource.totalNumberOfResults!;
    }

    const onSelectChange = (newSelectedRowKeys: Key[], selectedRows: T[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        if (onSelectionCallback) {
            onSelectionCallback(selectedRows);
        }
    };

    const rowSelection = hasAccess
        ? {
              selectedRowKeys,
              onChange: onSelectChange,
          }
        : undefined;

    return (
        <Table
            id={name}
            loading={datasource?.isFetching}
            rowSelection={rowSelection}
            columns={columnConfig}
            dataSource={addKeyProp<T>((datasource?.data || []) as readonly T[])}
            onChange={onChange}
            pagination={paginationFinal}
            onRow={hasAccess ? onRow : undefined}
            className="aevi-table"
            rowClassName={rowClassName}
        />
    );
};

function extendColumnConfig<T>(columns: ExtendedColumnsType<T>[], data: ReadonlyArray<T>): ColumnType<T>[] {
    return columns.map((column) => {
        const { dataIndex, useUniqueValuesAsFilter } = column;

        if (useUniqueValuesAsFilter && dataIndex) {
            return addUniqueValuesAsFilters<T>(column, data);
        }

        return column;
    }) as ColumnType<T>[];
}

function addUniqueValuesAsFilters<T>(column: ExtendedColumnsType<T>, data: ReadonlyArray<T>) {
    const { dataIndex } = column;

    const filters = new Set();

    const columnClone = { ...column };

    delete columnClone.useUniqueValuesAsFilter;

    columnClone.filters = data
        .map((row) => {
            // @ts-ignore
            const val = row[dataIndex as string];
            if (val && !filters.has(val)) {
                filters.add(val);
                return { text: val, value: val };
            }

            return null;
        })
        .filter(Boolean) as ColumnFilterItem[];

    return columnClone;
}

// antd table requires to have a key prop in the table's row object, otherwise
// it will log warning in the console
function addKeyProp<T extends Entity>(array: ReadonlyArray<T>) {
    return array.map((obj) => {
        return { ...obj, key: obj.id };
    });
}
